import BusinessIcon from '@mui/icons-material/Business';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Theme,
} from '@mui/material';
import Tab from '@mui/material/Tab';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import { PhoneNumberUtil } from 'google-libphonenumber';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilStateLoadable } from 'recoil';
import {
  useAddProfileBrandMutation,
  useUpsertProfileBrandMutation,
} from 'src/api/apigQuery';
import { EINCheck, WebsiteURLCheck } from 'src/app/constants/regex';
import * as Yup from 'yup';

import { LabelledOutline } from '../../../common/final-form-material-ui';
import { MaterialAutocomplete } from '../../../common/formik-material-ui';
import { FormikSelect } from '../../../common/formik-material-ui/FormikSelect';
import { FormikTextField } from '../../../common/formik-material-ui/FormikTextField';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import { GenericSkeleton } from '../../modules/Skeleton/GenericSkeleton';
import TabLabel from '../general/TabLabel';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
// import { countries as countryList } from '../countries';
// import { states as stateList } from '../states';
import { regions as regionList } from '../regions';
import CampaignRegistration from '../tenDLC/CampaignRegistration';

import { getErrorTabIndex, isErrorInTab } from './DevicesAndRoutingModal';
import { WizardModal } from './WizardModal';

const phoneUtil = PhoneNumberUtil.getInstance();

const useStyles = makeStyles((theme: Theme) => ({
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  tabIcon: {
    marginRight: 15,
  },
  tabs: {},
  reset: {
    margin: 0,
    padding: 0,
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  warningIcon: {
    color: 'orange',
  },
}));

export default function RegisterBrand(props) {
  const [upsertProfilebrand] = useUpsertProfileBrandMutation();
  const [addNewProfileBrand] = useAddProfileBrandMutation();
  const [userLoadable] = useRecoilStateLoadable(user);
  const userContents = userLoadable.contents as IUserAtom;
  const {
    profileBrandsLoading,
    profileBrandsSuccess,
    businessProfileBrands,
    campaignTemplate,
    brandData,
  } = props;
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [tabValue, setTabValue] = useState<number>(0);
  useEffect(() => {
    tabValue !== 0 && setTabValue(0);
    return () => setTabValue(0);
  }, []);
  const [tabIndex, setTabIndex] = useState(0);
  const isEditing = !props.isNewBrand && !!brandData?.brandId;
  const handleChange = (formik: FormikProps<any>) => async (
    event: any,
    newIndex: number
  ) => {
    setTabIndex(+newIndex);
  };

  const registerBrandSchema = useMemo(
    () =>
      Yup.object({
        employerId: Yup.string().when('einIssuingCountry', {
          is: (einIssuingCountry) => einIssuingCountry === 'US',
          then: Yup.string()
            .matches(EINCheck, 'EIN format invalid')
            .required('EIN is required'),
          otherwise: Yup.string()
            .required('Tax ID is required')
            .max(40, 'Tax ID must be 40 characters or less'),
        }),
        einIssuingCountry: Yup.string().required(
          'EIN Issuing Country is required'
        ),
        companyName: Yup.string().required('required'),
        brandAlias: Yup.string().required('required'),
        phone: Yup.string()
          .required('Required')
          .matches(
            /^[1-9]\d{1,14}$/g,
            'Invalid Phone Number. Please use E164 phone number format.'
          )
          .isValidE164Number(
            'Phone number must match the E164 format of selected country code'
          )
          .when('countrycode', (countrycode, field) =>
            field.test({
              test: (phone) => {
                if (!phone) return false;
                try {
                  const number = phoneUtil.parseAndKeepRawInput(
                    phone,
                    countrycode
                  );
                  const countryCodeForNumber = number.getCountryCode();
                  return phone.startsWith(String(countryCodeForNumber));
                } catch {
                  return false;
                }
              },
              message: `Number must start with correct country code.`,
            })
          ),
        countrycode: Yup.string().required('required'),
        website: Yup.string()
          .matches(WebsiteURLCheck, 'Invalid Website.')
          .required('required'),
        entityType: Yup.string().required('required'),
        stockSymbol: Yup.string().when('entityType', {
          is: 'PUBLIC',
          then: (schema) =>
            schema
              .required('Stock Symbol is required for PUBLIC entities')
              .min(1, 'Stock Symbol cannot be empty'),
          otherwise: (schema) => schema,
        }),
        stockExchange: Yup.string().when('entityType', {
          is: 'PUBLIC',
          then: (schema) =>
            schema
              .required('Stock Exchange is required for PUBLIC entities')
              .test(
                'not-none',
                'Stock Exchange cannot be "NONE" for PUBLIC entities',
                (value) => {
                  return value !== 'NONE' && value !== '' && !!value;
                }
              ),
          otherwise: (schema) => schema,
        }),
        vertical: Yup.string().required('required'),
        companyAddress: Yup.object().shape({
          city: Yup.string().required('required'),
          state: Yup.string().required('required'),
          street: Yup.string().required('required'),
          postalCode: Yup.string().required('required'),
        }),
        authorizedContact: Yup.object().shape({
          title: Yup.string().required('required'),
          firstName: Yup.string().required('required'),
          lastName: Yup.string().required('required'),
          email: Yup.string().email('Invalid Email').required('required'),
          seniority: Yup.string().required('required'),
        }),
        campaign: Yup.object().shape({
          campaignAlias: Yup.string().required('required'),
          description: Yup.string()
            .min(40, 'Minimum 40 characters required for Campaign Description')
            .required('required'),
          usecase: Yup.string().required('required'),
          sample1: Yup.string()
            .min(20, 'Minimum 20 characters required for Sample Message #1')
            .required('required'),
          sample2: Yup.string()
            .min(20, 'Minimum 20 characters required for Sample Message #2')
            .required('required'),
          messageFlow: Yup.string()
            .min(
              40,
              'Minimum 40 characters required for SMS Opt-In Process Proof'
            )
            .max(
              320,
              'Maximum 320 characters required for SMS Opt-In Process Proof'
            )
            .required('required'),
          subUsecases: Yup.object().shape({
            F2A: Yup.bool(),
            CUSTOMER_CARE: Yup.bool(),
            HIGHER_EDUCATION: Yup.bool(),
            MARKETING: Yup.bool(),
            ACCOUNT_NOTIFICATION: Yup.bool(),
            POLLING_VOTING: Yup.bool(),
            PUBLIC_SERVICE_ANNOUNCEMENT: Yup.bool(),
            SECURITY_ALERT: Yup.bool(),
            DELIVERY_NOTIFICATION: Yup.bool(),
            FRAUD_ALERT: Yup.bool(),
          }),
        }),
      }),
    []
  );

  const campaignTemplateData = useMemo(() => {
    if (campaignTemplate?.kixieFormJson && campaignTemplate?.plivoReturnJson) {
      return {
        ...campaignTemplate,
        kixieCampaignFormJson:
          JSON.parse(campaignTemplate?.kixieFormJson) ?? {},
        plivoMainCampaignJson:
          JSON.parse(campaignTemplate?.plivoReturnJson) ?? {},
      };
    }
  }, [campaignTemplate]);

  const verticalList = [
    { viewValue: 'Professional', value: 'PROFESSIONAL' },
    { viewValue: 'Real Estate', value: 'REAL_ESTATE' },
    { viewValue: 'Healthcare', value: 'HEALTHCARE' },
    { viewValue: 'Human Resources', value: 'HUMAN_RESOURCES' },
    { viewValue: 'Energy', value: 'ENERGY' },
    { viewValue: 'Entertainment', value: 'ENTERTAINMENT' },
    { viewValue: 'Retail', value: 'RETAIL' },
    { viewValue: 'Transportation', value: 'TRANSPORTATION' },
    { viewValue: 'Agriculture', value: 'AGRICULTURE' },
    { viewValue: 'Insurance', value: 'INSURANCE' },
    { viewValue: 'Postal', value: 'POSTAL' },
    { viewValue: 'Education', value: 'EDUCATION' },
    { viewValue: 'Hospitality', value: 'HOSPITALITY' },
    { viewValue: 'Financial', value: 'FINANCIAL' },
    { viewValue: 'Political', value: 'POLITICAL' },
    { viewValue: 'Gambling', value: 'GAMBLING' },
    { viewValue: 'Legal', value: 'LEGAL' },
    { viewValue: 'Construction', value: 'CONSTRUCTION' },
    { viewValue: 'NGO', value: 'NGO' },
    { viewValue: 'Manufacturing', value: 'MANUFACTURING' },
    { viewValue: 'Government', value: 'GOVERNMENT' },
    { viewValue: 'Technology', value: 'TECHNOLOGY' },
    { viewValue: 'Communication', value: 'COMMUNICATION' },
  ];

  const stockExchangeValues = ['NONE', 'NASDAQ', 'NYSE', 'AMEX', 'AMX', 'ASX'];

  const checkSubUsecase = (subusecase: string, campaignTemplateData: any) => {
    return campaignTemplateData?.plivoMainCampaignJson?.subUsecases?.indexOf(
      subusecase
    ) !== undefined
      ? campaignTemplateData?.plivoMainCampaignJson?.subUsecases?.indexOf(
          subusecase
        ) !== -1
      : campaignTemplateData?.kixieCampaignFormJson?.subUsecases?.indexOf(
          subusecase
        ) !== undefined
      ? campaignTemplateData?.kixieCampaignFormJson?.subUsecases?.indexOf(
          subusecase
        ) !== -1
      : false;
  };
  const initialValues = React.useMemo(
    () =>
      !profileBrandsLoading &&
      profileBrandsSuccess &&
      brandData &&
      !props?.isNewBrand
        ? {
            profileAlias:
              brandData?.plivoProfileJson?.profile?.profileAlias ??
              brandData?.kixieProfileFormJson?.profileAlias ??
              '',
            companyName:
              brandData?.plivoProfileJson?.profile?.companyName ??
              brandData?.kixieProfileFormJson?.companyName ??
              '',
            brandAlias:
              brandData?.plivoProfileJson?.profile?.brandAlias ??
              brandData?.kixieProfileFormJson?.brandAlias ??
              '',
            brandName: '',
            brandStatus: 'STANDARD',
            employerId:
              brandData?.plivoProfileJson?.profile?.ein ??
              brandData?.kixieProfileFormJson?.employerId ??
              '',
            einIssuingCountry:
              brandData?.plivoProfileJson?.profile?.einIssuingCountry ??
              brandData?.kixieProfileFormJson?.einIssuingCountry ??
              'US',
            entityType:
              (brandData?.plivoProfileJson?.profile?.entityType ===
                'PRIVATE_PROFIT' ||
              brandData?.kixieProfileFormJson?.entityType === 'PRIVATE_PROFIT'
                ? 'PRIVATE'
                : brandData?.plivoProfileJson?.profile?.entityType ??
                  brandData?.kixieProfileFormJson?.entityType) ?? '',
            vertical:
              brandData?.plivoProfileJson?.profile?.vertical ??
              brandData?.kixieProfileFormJson?.vertical ??
              '',
            companyAddress: {
              city:
                brandData?.plivoProfileJson?.profile?.address?.city ??
                brandData?.kixieProfileFormJson?.companyAddress?.city ??
                '',
              state:
                brandData?.plivoProfileJson?.profile?.address?.state ??
                brandData?.kixieProfileFormJson?.companyAddress?.state ??
                '',
              street:
                brandData?.plivoProfileJson?.profile?.address?.street ??
                brandData?.kixieProfileFormJson?.companyAddress?.street ??
                '',
              postalCode:
                brandData?.plivoProfileJson?.profile?.address?.postalCode ??
                brandData?.kixieProfileFormJson?.companyAddress?.postalCode ??
                '',
            },
            countrycode:
              brandData?.plivoProfileJson?.profile?.address?.country ??
              brandData?.kixieProfileFormJson?.companyAddress?.country ??
              'US',
            phone:
              brandData?.plivoProfileJson?.profile?.authorizedContact?.phone ??
              brandData?.kixieProfileFormJson?.authorizedContact?.phone ??
              '',
            website:
              brandData?.plivoProfileJson?.profile?.website ??
              brandData?.kixieProfileFormJson?.website ??
              '',
            authorizedContact: {
              title:
                brandData?.plivoProfileJson?.profile?.authorizedContact
                  ?.title ??
                brandData?.kixieProfileFormJson?.authorizedContact?.title ??
                '',
              firstName:
                brandData?.plivoProfileJson?.profile?.authorizedContact
                  ?.firstName ??
                brandData?.kixieProfileFormJson?.authorizedContact?.firstName ??
                '',
              lastName:
                brandData?.plivoProfileJson?.profile?.authorizedContact
                  ?.lastName ??
                brandData?.kixieProfileFormJson?.authorizedContact?.lastName ??
                '',
              email:
                brandData?.plivoProfileJson?.profile?.authorizedContact
                  ?.email ??
                brandData?.kixieProfileFormJson?.authorizedContact?.email ??
                '',
              seniority:
                brandData?.plivoProfileJson?.profile?.authorizedContact
                  ?.seniority ??
                brandData?.kixieProfileFormJson?.authorizedContact?.seniority ??
                'OTHER',
            },
            stockSymbol:
              brandData?.plivoProfileJson?.profile?.stockSymbol ??
              brandData?.kixieProfileFormJson?.stockSymbol ??
              '',
            stockExchange:
              brandData?.plivoProfileJson?.profile?.stockExchange ??
              brandData?.kixieProfileFormJson?.stockExchange ??
              '',
            campaign: {
              usecase:
                campaignTemplateData?.plivoMainCampaignJson?.usecase ??
                campaignTemplateData?.kixieCampaignFormJson?.usecase ??
                '',
              subUsecases: {
                F2A: checkSubUsecase('2FA', campaignTemplateData),
                CUSTOMER_CARE: checkSubUsecase(
                  'CUSTOMER_CARE',
                  campaignTemplateData
                ),
                HIGHER_EDUCATION: checkSubUsecase(
                  'HIGHER_EDUCATION',
                  campaignTemplateData
                ),
                MARKETING: checkSubUsecase('MARKETING', campaignTemplateData),
                ACCOUNT_NOTIFICATION: checkSubUsecase(
                  'ACCOUNT_NOTIFICATION',
                  campaignTemplateData
                ),
                POLLING_VOTING: checkSubUsecase(
                  'POLLING_VOTING',
                  campaignTemplateData
                ),
                PUBLIC_SERVICE_ANNOUNCEMENT: checkSubUsecase(
                  'PUBLIC_SERVICE_ANNOUNCEMENT',
                  campaignTemplateData
                ),
                SECURITY_ALERT: checkSubUsecase(
                  'SECURITY_ALERT',
                  campaignTemplateData
                ),
                DELIVERY_NOTIFICATION: checkSubUsecase(
                  'DELIVERY_NOTIFICATION',
                  campaignTemplateData
                ),
                FRAUD_ALERT: checkSubUsecase(
                  'FRAUD_ALERT',
                  campaignTemplateData
                ),
              },
              campaignAlias:
                campaignTemplateData?.plivoMainCampaignJson?.campaignAlias ??
                campaignTemplateData?.kixieCampaignFormJson?.campaignAlias ??
                '',
              description:
                campaignTemplateData?.plivoMainCampaignJson?.description ??
                campaignTemplateData?.kixieCampaignFormJson?.description ??
                '',
              messageFlow:
                campaignTemplateData?.plivoMainCampaignJson?.messageFlow ??
                campaignTemplateData?.kixieCampaignFormJson?.messageFlow ??
                '',
              sample1:
                campaignTemplateData?.plivoMainCampaignJson?.sample1 ??
                campaignTemplateData?.kixieCampaignFormJson?.sample1 ??
                '',
              sample2:
                campaignTemplateData?.plivoMainCampaignJson.sample2 ??
                campaignTemplateData?.kixieCampaignFormJson?.sample2 ??
                '',
              attributes: campaignTemplateData?.plivoMainCampaignJson
                ?.attributes ??
                campaignTemplateData?.kixieCampaignFormJson?.attributes ?? {
                  subscriberOptin: 1,
                  subscriberOptout: 1,
                  subscriberHelp: 1,
                  directLending: 0,
                  embeddedLink: 0,
                  embeddedPhone: 0,
                  affiliateMarketing: 0,
                  ageGated: 0,
                },
            },
          }
        : {
            profileAlias: '',
            companyName: '',
            brandAlias: '',
            employerId: '',
            einIssuingCountry: 'US',
            brandStatus: 'STANDARD',
            entityType: '',
            vertical: '',
            companyAddress: {
              city: '',
              state: '',
              street: '',
              postalCode: '',
            },
            companyPhone: '',
            phone: '',
            countrycode: 'US',
            website: '',
            authorizedContact: {
              title: '',
              firstName: '',
              lastName: '',
              email: '',
              seniority: 'OTHER',
            },
            stockSymbol: '',
            stockExchange: '',
            campaign: {
              usecase: '',
              subUsecases: {
                F2A: false,
                CUSTOMER_CARE: false,
                HIGHER_EDUCATION: false,
                MARKETING: false,
                ACCOUNT_NOTIFICATION: false,
                POLLING_VOTING: false,
                PUBLIC_SERVICE_ANNOUNCEMENT: false,
                SECURITY_ALERT: false,
                DELIVERY_NOTIFICATION: false,
                FRAUD_ALERT: false,
              },
              campaignAlias: '',
              description: '',
              messageFlow: '',
              sample1: '',
              sample2: '',
              attributes: {
                subscriberOptin: 1,
                subscriberOptout: 1,
                subscriberHelp: 1,
                directLending: 0,
                embeddedLink: 0,
                embeddedPhone: 0,
                affiliateMarketing: 0,
                ageGated: 0,
              },
            },
          },
    [brandData, campaignTemplateData, props.isNewBrand]
  );
  const tabToFields = [
    [
      'profileAlias',
      'companyName',
      'brandAlias',
      'employerId',
      'einIssuingCountry',
      'entityType',
      'vertical',
      'companyAddress',
      'phone',
      'countrycode',
      'website',
      'authorizedContact',
      'stockSymbol',
      'stockExchange',
    ],
    ['campaign'],
  ];
  const classes = useStyles();
  const tabNames = [
    {
      title: 'Register Your Brand',
      icon: BusinessIcon,
    },
    {
      title: 'Campaign Registration',
      icon: LibraryAddIcon,
    },
  ];

  return (
    <Formik
      enableReinitialize={true}
      validateOnChange
      validationSchema={registerBrandSchema}
      onSubmit={async (values, actions) => {
        const valuesToSend: any = { ...values };
        const authorizedContact = {
          ...valuesToSend.authorizedContact,
          phone: valuesToSend.phone,
        };
        const companyAddress = {
          ...valuesToSend.companyAddress,
          country: valuesToSend.countrycode,
        };
        delete valuesToSend.phone;
        delete valuesToSend.authorizedContact;
        delete valuesToSend.companyAddress;
        delete valuesToSend.countrycode;
        const res = JSON.stringify({
          ...valuesToSend,
          authorizedContact: authorizedContact,
          companyAddress: companyAddress,
          employerId_issuing_country: values?.einIssuingCountry ?? '',
        });
        const subs =
          valuesToSend.campaign.usecase === 'MIXED' ||
          valuesToSend.campaign.usecase === 'LOW_VOLUME'
            ? Object.keys(valuesToSend?.campaign?.subUsecases)
                .map((keyName, i) => {
                  if (valuesToSend?.campaign?.subUsecases?.[keyName] === true) {
                    return keyName === 'F2A' ? '2FA' : keyName;
                  }
                })
                ?.filter?.((el) => el !== undefined)
            : [];
        const campaign = JSON.stringify({
          ...valuesToSend?.campaign,
          subUsecases: subs ?? [],
          kixieId: campaignTemplate?.kixieCampaignId,
        });
        let originalData;
        if (!props.isNewBrand && brandData?.brandId) {
          originalData = businessProfileBrands?.result?.find(
            (el) => el.brandId === brandData.brandId
          );
        }
        const updatedBrand = {
          ...(originalData || {}), //TODO fix the api call so we don't have to send this, we should get it in the backend [ENG-8324]
          kixieProfileFormJson: res,
          kixieCampaignFormJson: campaign,
          plivoMainCampaignJson: campaignTemplate?.plivoReturnJson,
        };

        try {
          let response;
          if (props.isNewBrand) {
            response = await addNewProfileBrand({
              userSettings: {
                apikey: userContents?.apikey,
                businessId: userContents?.businessid,
                userid: userContents?.userid,
              },
              updatedBrand: updatedBrand,
            });
          } else {
            response = await upsertProfilebrand({
              userSettings: {
                apikey: userContents?.apikey,
                businessId: userContents?.businessid,
                userid: userContents?.userid,
              },
              updatedBrand: updatedBrand,
            });
          }
          if (
            response.data.success === false ||
            response?.data?.error ||
            response?.data?.statusText?.toLowerCase() === 'bad request'
          ) {
            enqueueSnackbarWithStyles({
              message: response?.data?.error ?? `Error with 10DLC registration`,
              alertProps: { severity: 'error' },
            });
          } else {
            enqueueSnackbarWithStyles({
              message: `10DLC registration successfully updated`,
              alertProps: { severity: 'success' },
            });
          }
        } catch (err) {
          enqueueSnackbarWithStyles({
            message: `Error with 10DLC registration`,
            alertProps: { severity: 'error' },
          });
        }
        props.setRefetchToggleForProfileBrands();
        props.setRefetchToggleForCampaigns();
        actions.resetForm({});
        props.handleClose();
      }}
      initialValues={initialValues}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit}>
          <TabContext value={String(tabIndex)}>
            <WizardModal
              title={tabNames?.[tabIndex]?.title ?? 0}
              // disableSubmit={!formik.isValid}
              navigation={
                <>
                  <Tab
                    label={'10DLC Registration'}
                    disabled={true}
                    className={clsx(classes.tab)}
                  />
                  <TabList
                    onChange={handleChange(formik)}
                    aria-label="10DLC Registration"
                    orientation="vertical"
                    variant="standard"
                    className={clsx(classes.tabs, {})}
                  >
                    {tabNames.map((tabItem, index) => (
                      <Tab
                        // name={tabItem.title}
                        key={index}
                        className={classes.tab}
                        label={
                          <TabLabel
                            index={index}
                            formik={formik}
                            tabToFields={tabToFields}
                            tabItem={tabItem}
                          />
                        }
                        value={String(index)}
                      />
                    ))}
                  </TabList>
                </>
              }
              dialogActions={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Button
                    disabled={formik.isSubmitting}
                    variant="contained"
                    color="inherit"
                    disableElevation
                    onClick={props.handleClose}
                  >
                    Cancel
                  </Button>
                  {tabIndex === 0 ? (
                    <Button
                      className={classes.button}
                      color="primary"
                      variant="contained"
                      disableElevation
                      disabled={Boolean(
                        isErrorInTab(0, formik.errors, tabToFields)
                      )}
                      onClick={(event: any) => {
                        setTabIndex(1);
                        // formik.handleSubmit(event);

                        //   if (
                        //     !formik.isValid && // if form invalid
                        //     !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                        //   ) {
                        //     const errorTabIndex = getErrorTabIndex(
                        //       formik.errors,
                        //       tabToFields
                        //     );
                        //     setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                        //   }
                      }}
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      className={classes.button}
                      variant="contained"
                      disableElevation={true}
                      disabled={
                        formik.isSubmitting || !formik.dirty || !formik.isValid
                      }
                      onClick={(event: any) => {
                        formik.handleSubmit(event);

                        if (
                          !formik.isValid && // if form invalid
                          !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                        ) {
                          const errorTabIndex = getErrorTabIndex(
                            formik.errors,
                            tabToFields
                          );
                          setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                        }
                      }}
                    >
                      Submit
                    </Button>
                  )}
                </div>
              }
              size="lg"
              show={props.show}
              onHide={() => {
                props.handleClose();
                formik.resetForm({});
              }}
              onSubmit={formik.handleSubmit}
              isLoading={
                formik.isSubmitting || (profileBrandsLoading && isEditing)
              }
              isSubmitting={formik.isSubmitting}
              disableSubmit={
                !formik.isValid ||
                formik.isSubmitting ||
                (profileBrandsLoading && isEditing)
              }
            >
              <TabPanel value="0">
                <Box>
                  {!(
                    isEditing &&
                    (profileBrandsLoading || !profileBrandsSuccess)
                  ) ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <LabelledOutline label="Contact Information">
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="authorizedContact.title"
                                formik={formik}
                                fullWidth
                                label="Salutation"
                                size="small"
                                variant="outlined"
                                required
                                helperText={'Salutation of the contact'}
                                placeholder="Mr"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikSelect
                                name="authorizedContact.seniority"
                                formik={formik}
                                fullWidth
                                label="Title"
                                size="small"
                                variant="outlined"
                                required
                              >
                                <MenuItem value="DIRECTOR">Director</MenuItem>
                                <MenuItem value="GM">GM</MenuItem>
                                <MenuItem value="VP">VP</MenuItem>
                                <MenuItem value="CEO">CEO</MenuItem>
                                <MenuItem value="INDIVIDUAL">CFO</MenuItem>
                                <MenuItem value="GENERAL_COUNSEL">
                                  General Counsel
                                </MenuItem>
                                <MenuItem value="OTHER">Other</MenuItem>
                              </FormikSelect>
                              <FormHelperText
                                style={{
                                  fontSize: '0.75rem',
                                  margin: '4px 14px 0px',
                                }}
                              >
                                Business contact's job title
                              </FormHelperText>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="authorizedContact.firstName"
                                formik={formik}
                                fullWidth
                                label="First Name"
                                size="small"
                                variant="outlined"
                                required
                                helperText="First name of your business contact"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="authorizedContact.lastName"
                                formik={formik}
                                fullWidth
                                label="Last Name"
                                size="small"
                                required
                                variant="outlined"
                                helperText="Last name of your business contact"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <MaterialAutocomplete
                                name="countrycode"
                                formik={formik}
                                disableClearable
                                type="text"
                                fullWidth
                                label="Country"
                                size="small"
                                variant="outlined"
                                defaultValue={'US'}
                                renderOption={(props, option: any) => {
                                  const countryName =
                                    regionList?.find?.(
                                      (country) =>
                                        country?.countryShortCode === option
                                    )?.countryName ?? '';

                                  return <li {...props}>{countryName}</li>;
                                }}
                                getOptionLabel={(option: any) =>
                                  regionList?.find?.(
                                    (country) =>
                                      country?.countryShortCode === option
                                  )?.countryName ?? ''
                                }
                                options={regionList.map(
                                  (country) => country.countryShortCode
                                )}
                                onChange={(event, value, reason) => {
                                  formik.setFieldValue(
                                    'companyAddress.state',
                                    ''
                                  );
                                  formik.setFieldValue('countrycode', value);
                                  formik.setFieldValue(
                                    'einIssuingCountry',
                                    value
                                  );
                                  setTimeout(() => {
                                    formik.setFieldTouched('phone', true);
                                    formik.setFieldTouched(
                                      'companyAddress.state',
                                      true
                                    );
                                  });
                                }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="phone"
                                formik={formik}
                                fullWidth
                                label="Phone"
                                size="small"
                                variant="outlined"
                                required
                                error={!!formik?.errors?.phone}
                                helperText="Business contact’s phone number"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      +
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="authorizedContact.email"
                                formik={formik}
                                fullWidth
                                label="Email"
                                size="small"
                                variant="outlined"
                                required
                                helperText="Business contact’s email address"
                              />
                            </Grid>
                          </Grid>
                        </LabelledOutline>
                      </Grid>

                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <LabelledOutline label="Business Information">
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                              <FormikTextField
                                name="companyName"
                                formik={formik}
                                fullWidth
                                required
                                label="Legal Company Name"
                                size="small"
                                variant="outlined"
                                placeholder="must match the public record for your Tax ID/EIN"
                                InputLabelProps={{ shrink: true }}
                              />
                              <div
                                style={{
                                  color: 'rgba(0, 0, 0, 0.54)',
                                  fontSize: '0.75rem',
                                  margin: '4px 14px 0px',
                                }}
                              >
                                <span>
                                  It's important your Legal Company Name is
                                  exactly correct. Lookup most US companies
                                </span>{' '}
                                <a
                                  rel="noreferrer"
                                  target="_blank"
                                  href="https://www.hipaaspace.com/ein/ein_verification/"
                                >
                                  here
                                </a>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="brandAlias"
                                formik={formik}
                                fullWidth
                                required
                                label="Doing Business As"
                                size="small"
                                variant="outlined"
                                helperText="This should match your business's 'Doing
                                  Business As' name."
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="website"
                                formik={formik}
                                fullWidth
                                required
                                label="Company Website"
                                size="small"
                                variant="outlined"
                                helperText={'Website URL'}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <MaterialAutocomplete
                                name="vertical"
                                formik={formik}
                                disableClearable
                                type="text"
                                fullWidth
                                label="Vertical"
                                size="small"
                                variant="outlined"
                                required
                                defaultValue={formik?.values?.vertical ?? ''}
                                getOptionLabel={(option: any) => {
                                  return (
                                    verticalList?.find?.(
                                      (vertical) => vertical?.value === option
                                    )?.viewValue ?? ''
                                  );
                                }}
                                isOptionEqualToValue={(option, anotherOption) =>
                                  option === anotherOption
                                }
                                options={verticalList?.map(
                                  (vertical) => vertical.value
                                )}
                                // helperText={'Business Industry'}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikSelect
                                name="entityType"
                                formik={formik}
                                fullWidth
                                label="Entity Type"
                                size="small"
                                variant="outlined"
                                required
                              >
                                <MenuItem value="PRIVATE">Private</MenuItem>
                                <MenuItem value="PUBLIC">Public</MenuItem>
                                <MenuItem value="NON_PROFIT">
                                  Non Profit
                                </MenuItem>
                                <MenuItem value="GOVERNMENT">
                                  Government
                                </MenuItem>
                                {/* <MenuItem value="INDIVIDUAL">Individual</MenuItem> */}
                              </FormikSelect>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <MaterialAutocomplete
                                name="einIssuingCountry"
                                formik={formik}
                                disableClearable
                                type="text"
                                fullWidth
                                label="Tax ID/EIN Issuing Country"
                                size="small"
                                variant="outlined"
                                required
                                defaultValue={
                                  formik?.values?.einIssuingCountry ?? 'US'
                                }
                                renderOption={(props, option: any) => {
                                  const countryName =
                                    regionList?.find?.(
                                      (country) =>
                                        country?.countryShortCode === option
                                    )?.countryName ?? '';

                                  return <li {...props}>{countryName}</li>;
                                }}
                                getOptionLabel={(option: any) =>
                                  regionList?.find?.(
                                    (country) =>
                                      country?.countryShortCode === option
                                  )?.countryName ?? ''
                                }
                                options={regionList.map(
                                  (country) => country.countryShortCode
                                )}
                                onChange={(event, value, reason) => {
                                  formik.setFieldValue(
                                    'companyAddress.state',
                                    ''
                                  );
                                  formik.setFieldValue(
                                    'einIssuingCountry',
                                    value
                                  );
                                  formik.setFieldValue('countrycode', value);
                                  setTimeout(() => {
                                    formik.setFieldTouched('phone', true);
                                    formik.setFieldTouched(
                                      'companyAddress.state',
                                      true
                                    );
                                  });
                                }}
                                // helperText={'Country of registration'}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="employerId"
                                formik={formik}
                                fullWidth
                                label="Tax ID/EIN"
                                size="small"
                                required
                                variant="outlined"
                                helperText={'Business’s tax number / ID / EIN'}
                              />
                            </Grid>
                            {formik?.values?.entityType === 'PUBLIC' && (
                              <>
                                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                                  <FormikTextField
                                    name="stockSymbol"
                                    formik={formik}
                                    fullWidth
                                    label="Stock Symbol"
                                    size="small"
                                    variant="outlined"
                                    required
                                    helperText="Stock Symbol - Company's stock symbol (required for PUBLIC entities)"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                                  <FormikSelect
                                    name="stockExchange"
                                    formik={formik}
                                    fullWidth
                                    label="Stock Exchange"
                                    size="small"
                                    variant="outlined"
                                    required
                                  >
                                    {stockExchangeValues
                                      .filter((el) => el !== 'NONE')
                                      .map((el, i) => (
                                        <MenuItem key={`${el}-${i}`} value={el}>
                                          {el}
                                        </MenuItem>
                                      ))}
                                  </FormikSelect>
                                  <div
                                    style={{
                                      color: 'rgba(0, 0, 0, 0.54)',
                                      fontSize: '0.75rem',
                                      margin: '4px 14px 0px',
                                    }}
                                  >
                                    <span>
                                      On which stock exchange is your company
                                      listed?
                                    </span>
                                  </div>
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </LabelledOutline>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <LabelledOutline label="Company Address">
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="companyAddress.street"
                                formik={formik}
                                fullWidth
                                label="Address"
                                size="small"
                                variant="outlined"
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="companyAddress.city"
                                formik={formik}
                                fullWidth
                                label="City"
                                size="small"
                                variant="outlined"
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              {[
                                'KY',
                                'CK',
                                'FK',
                                'FO',
                                'GF',
                                'PF',
                                'TF',
                                'GH',
                                'GI',
                                'GP',
                                'GU',
                                'GG',
                                'HM',
                                'IM',
                                'JE',
                                'KZ',
                                'KI',
                                'MO',
                                'MQ',
                                'MS',
                                'NC',
                                'NU',
                                'NF',
                                'MP',
                                'PN',
                                'PR',
                                'RE',
                                'MF',
                                'PM',
                                'SX',
                                'GS',
                                'TK',
                                'TC',
                              ].indexOf(formik?.values?.countrycode) === -1 ? (
                                <MaterialAutocomplete
                                  name="companyAddress.state"
                                  formik={formik}
                                  disableClearable
                                  type="text"
                                  fullWidth
                                  label="State"
                                  size="small"
                                  variant="outlined"
                                  error={
                                    !!formik?.errors?.companyAddress?.state
                                  }
                                  required={
                                    [
                                      'KY',
                                      'CK',
                                      'FK',
                                      'FO',
                                      'GF',
                                      'PF',
                                      'TF',
                                      'GH',
                                      'GI',
                                      'GP',
                                      'GU',
                                      'GG',
                                      'HM',
                                      'IM',
                                      'JE',
                                      'KZ',
                                      'KI',
                                      'MO',
                                      'MQ',
                                      'MS',
                                      'NC',
                                      'NU',
                                      'NF',
                                      'MP',
                                      'PN',
                                      'PR',
                                      'RE',
                                      'MF',
                                      'PM',
                                      'SX',
                                      'GS',
                                      'TK',
                                      'TC',
                                    ].indexOf(formik?.values?.countrycode) ===
                                    -1
                                  }
                                  defaultValue={
                                    formik?.values?.companyAddress?.state ?? ''
                                  }
                                  renderOption={(props, option: any) => {
                                    const stateName =
                                      regionList
                                        ?.find(
                                          (region) =>
                                            region?.countryShortCode ===
                                            formik?.values?.countrycode
                                        )
                                        ?.regions?.find?.(
                                          (state) => state?.shortCode === option
                                        )?.name ?? '';

                                    return <li {...props}>{stateName}</li>;
                                  }}
                                  getOptionLabel={(option: any) =>
                                    regionList
                                      .find(
                                        (region) =>
                                          region?.countryShortCode ===
                                          formik?.values?.countrycode
                                      )
                                      ?.regions?.find?.(
                                        (state) => state?.shortCode === option
                                      )?.name ?? ''
                                  }
                                  options={
                                    regionList
                                      .find(
                                        (region) =>
                                          region?.countryShortCode ===
                                          formik?.values?.countrycode
                                      )
                                      ?.regions?.map(
                                        (regionState) => regionState.shortCode
                                      ) ?? []
                                  }
                                />
                              ) : (
                                <FormikTextField
                                  name="companyAddress.state"
                                  formik={formik}
                                  fullWidth
                                  label="State/Province"
                                  size="small"
                                  variant="outlined"
                                  error={
                                    !!formik?.errors?.companyAddress?.state
                                  }
                                  required={
                                    [
                                      'KY',
                                      'CK',
                                      'FK',
                                      'FO',
                                      'GF',
                                      'PF',
                                      'TF',
                                      'GH',
                                      'GI',
                                      'GP',
                                      'GU',
                                      'GG',
                                      'HM',
                                      'IM',
                                      'JE',
                                      'KZ',
                                      'KI',
                                      'MO',
                                      'MQ',
                                      'MS',
                                      'NC',
                                      'NU',
                                      'NF',
                                      'MP',
                                      'PN',
                                      'PR',
                                      'RE',
                                      'MF',
                                      'PM',
                                      'SX',
                                      'GS',
                                      'TK',
                                      'TC',
                                    ].indexOf(formik?.values?.countrycode) !==
                                    -1
                                  }
                                />
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <MaterialAutocomplete
                                name="countrycode"
                                formik={formik}
                                disableClearable
                                type="text"
                                fullWidth
                                label="Country"
                                size="small"
                                variant="outlined"
                                defaultValue={'US'}
                                renderOption={(props, option: any) => {
                                  const countryName =
                                    regionList?.find?.(
                                      (country) =>
                                        country?.countryShortCode === option
                                    )?.countryName ?? '';
                                  return <li {...props}>{countryName}</li>;
                                }}
                                getOptionLabel={(option: any) =>
                                  regionList?.find?.(
                                    (country) =>
                                      country?.countryShortCode === option
                                  )?.countryName ?? ''
                                }
                                options={regionList.map(
                                  (country) => country.countryShortCode
                                )}
                                onChange={(event, value, reason) => {
                                  formik.setFieldValue(
                                    'companyAddress.state',
                                    ''
                                  );
                                  formik.setFieldValue(
                                    'einIssuingCountry',
                                    value
                                  );
                                  formik.setFieldValue('countrycode', value);
                                  setTimeout(() => {
                                    formik.setFieldTouched('phone', true);
                                    formik.setFieldTouched(
                                      'companyAddress.state',
                                      true
                                    );
                                  });
                                }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                              <FormikTextField
                                name="companyAddress.postalCode"
                                formik={formik}
                                fullWidth
                                label="Postal Code"
                                size="small"
                                variant="outlined"
                                required
                              />
                            </Grid>
                          </Grid>
                        </LabelledOutline>
                      </Grid>
                    </Grid>
                  ) : (
                    <GenericSkeleton />
                  )}
                </Box>
              </TabPanel>
              <TabPanel value="1">
                <Box>
                  {!profileBrandsLoading && brandData ? (
                    <CampaignRegistration formik={formik} />
                  ) : (
                    <GenericSkeleton />
                  )}
                </Box>
              </TabPanel>
            </WizardModal>
          </TabContext>
        </form>
      )}
    </Formik>
  );
}
